/*
 * Wemond hero — inline Kahvemi Bul.
 *
 * Product matching is copied unchanged from assets/wemond-finder-v2.js
 * (prepareProduct / eligiblePool / scoreProduct / rankPool / package pick):
 * Q1 brew is the only hard filter, Q2–Q5 only score (35/30/25/10), ties keep
 * catalog order. What changes here is presentation: the flow runs inside the
 * hero instead of a modal, and each result card carries its own variant
 * (gram) state so the shown price, the variant id and the cart line all come
 * from the same selection.
 */
(() => {
  const ROOT = '[data-wmh-finder]';
  const OPEN_SELECTOR = '[data-wms-finder-open], a[href="#wemond-coffee-finder"], a[href="#wemond-finder"]';
  const DEEP_LINK_HASH = '#wemond-coffee-finder';
  const DEEP_LINK_QUERY_VALUE = 'coffee-finder';
  const CART_EVENT = 'shopify:cart:lines-update';

  const plain = (value) => Array.isArray(value) ? value.join(' ') : String(value ?? '');
  const esc = (value = '') => String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[c]));
  const money = (cents) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((Number(cents) || 0) / 100);
  const norm = (value) => plain(value).trim().toLocaleLowerCase('tr-TR');
  const lower = (value) => String(value || '').toLocaleLowerCase('tr-TR');
  const gramLabel = (title) => String(title || '').replace(/^\s*(\d+)\s*g\s*$/i, '$1 g');
  const cleanTitle = (title) => String(title || '').replace(/\s*-\s*\d+\s*g\s*$/i, '');
  const headerHeight = () => parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
  const gramValue = (title) => Number((String(title).match(/(\d+)\s*g/i) || [])[1] || 0);

  // ---- Matching (unchanged from wemond-finder-v2.js) ----------------------
  const BREW_VALUES = ['espresso', 'filter', 'both'];
  const TASTE_VALUES = ['fruity', 'balanced', 'strong'];
  const ACIDITY_VALUES = ['low', 'medium', 'high'];
  const BODY_VALUES = ['light', 'medium', 'high'];
  const QUESTION_ORDER = ['brew', 'flavor', 'acidity', 'body', 'discovery'];
  const SCORE_WEIGHTS = { taste: 35, acidity: 30, body: 25, discovery: 10 };

  const discoveryGroupFromProcess = (processValue) => {
    const text = norm(processValue);
    if (!text) return null;
    if (text.includes('anaerobic') || text.includes('sub-merged') || text.includes('submerged')) return 'adventurous';
    if (text.includes('natural')) return 'balanced';
    if (text.includes('washed')) return 'safe';
    return null;
  };

  const prepareProduct = (product) => {
    const meta = product.coffee_meta || {};
    const identity = norm(`${product.handle} ${product.title}`);
    const excluded = /deneyim.?paketi|experience.?pack/.test(identity);
    const brewRaw = norm(meta.brew);
    const brew = BREW_VALUES.includes(brewRaw) ? brewRaw : null;
    const taste = norm(meta.taste);
    const acidity = norm(meta.acidity);
    const body = norm(meta.body);
    const paths = brew === 'both' ? ['espresso', 'filter'] : (brew ? [brew] : []);
    // Only purchasable variants are ever offered. 250 g first (current default).
    const variants = (product.variants || []).filter((v) => v.available).sort((a, b) => gramValue(b.title) - gramValue(a.title));
    return {
      ...product, meta, excluded, paths, brew, variants,
      variant_id: variants[0]?.id || null,
      taste: TASTE_VALUES.includes(taste) ? taste : null,
      acidity: ACIDITY_VALUES.includes(acidity) ? acidity : null,
      body: BODY_VALUES.includes(body) ? body : null,
      discoveryGroup: discoveryGroupFromProcess(meta.process)
    };
  };

  const brewCompatible = (product, brewAnswer) => brewAnswer === 'both' ? product.brew === 'both' : product.paths.includes(brewAnswer);
  const eligiblePool = (catalog, brewAnswer) => catalog.filter((p) => !p.excluded && p.available && p.variant_id && brewCompatible(p, brewAnswer));
  const scoreProduct = (product, answers) => {
    let score = 0;
    if (answers.flavor && product.taste === answers.flavor) score += SCORE_WEIGHTS.taste;
    if (answers.acidity && product.acidity === answers.acidity) score += SCORE_WEIGHTS.acidity;
    if (answers.body && product.body === answers.body) score += SCORE_WEIGHTS.body;
    if (answers.discovery && product.discoveryGroup === answers.discovery) score += SCORE_WEIGHTS.discovery;
    return score;
  };
  const contextScore = (catalog, brewValue, answers) => eligiblePool(catalog, brewValue).reduce((sum, p) => sum + scoreProduct(p, answers), 0);
  const rankPool = (pool, answers) => [...pool].sort((a, b) => scoreProduct(b, answers) - scoreProduct(a, answers));

  // ---- Customer-facing copy ------------------------------------------------
  const QUESTIONS = {
    brew: { q: 'Kahveni nasıl hazırlıyorsun?', o: [
      ['espresso', 'Espresso', 'Espresso makinesi, tam otomatik makine, Moka Pot'],
      ['filter', 'Filtre', 'V60, Chemex, AeroPress, filtre makinesi, French Press'],
      ['both', 'İkisi de', 'Hem espresso hem filtre']] },
    flavor: { q: 'Fincanda hangi tadı daha çok seversin?', o: [
      ['balanced', 'Tatlı ve yumuşak', 'Çikolata, karamel, fındık gibi'],
      ['fruity', 'Meyvemsi', 'Kırmızı meyve, narenciye, çiçek gibi'],
      ['strong', 'Yoğun ve güçlü', 'Bitter çikolata, baharat gibi']] },
    acidity: { q: 'Kahvende meyvemsi, canlı tatları ne kadar seversin?', o: [
      ['low', 'Az olsun', 'Tatlılık ve yumuşaklık önde'],
      ['medium', 'Biraz olsun', 'Hafif canlı, dengeli'],
      ['high', 'Çok severim', 'Ferah ve belirgin']] },
    body: { q: 'Kahve ağızda nasıl hissettirsin?', o: [
      ['high', 'Dolgun ve yoğun', 'Ağzı dolduran, uzun kalan'],
      ['medium', 'Orta', 'Ne hafif ne ağır'],
      ['light', 'Hafif', 'Çay gibi akıcı ve temiz']] },
    discovery: { q: 'Yeni tatlar denemeye ne kadar açıksın?', o: [
      ['safe', 'Bildiğim tatlar', 'Klasik ve tutarlı'],
      ['balanced', 'Biraz farklı olabilir', 'Tanıdık ama yeni notalarla'],
      ['adventurous', 'Sıra dışı olsun', 'Deneysel işlemli, sürprizli kahveler']] }
  };
  const TASTE_WORD = { balanced: 'tatlı ve yumuşak', fruity: 'meyvemsi', strong: 'yoğun' };
  const BODY_WORD = { light: 'hafif gövdeli', medium: 'orta gövdeli', high: 'dolgun gövdeli' };
  const BREW_PHRASE = { espresso: 'Espresso demlemene uygun', filter: 'Filtre demlemene uygun', both: 'Hem espresso hem filtrede kullanılabilir' };

  // One sentence, built only from this product's real metafields and only
  // from the axes that actually matched the customer's answers.
  const reasonFor = (product, answers) => {
    const attrs = [];
    if (answers.flavor && product.taste === answers.flavor) attrs.push(TASTE_WORD[product.taste]);
    if (answers.body && product.body === answers.body) attrs.push(BODY_WORD[product.body]);
    const notes = plain(product.meta.tasting_notes).split(/[•·,]/).map((n) => n.trim()).filter(Boolean).slice(0, 2).map(lower);
    let sentence = BREW_PHRASE[answers.brew] || 'Seçimlerine uygun';
    if (attrs.length) sentence += `; ${attrs.join(' ve ')} bir kahve`;
    if (notes.length) sentence += `${attrs.length ? ',' : ';'} ${notes.join(' ve ')} notaları öne çıkıyor`;
    return `${sentence}.`;
  };

  const publish = (name, payload) => {
    // Shopify customer events (web pixels). Delivery to any pixel is decided
    // by Shopify's consent handling — nothing is sent from here directly.
    try { window.Shopify?.analytics?.publish?.(name, payload); } catch (_) {}
  };

  // SendWILL (ecomsend) discount popup: its trigger rules live in the app
  // (10 s on every page, store-wide) and cannot be changed from the theme.
  // While the shopper is inside Kahvemi Bul or the mini cart is open, ask the
  // app's own runtime not to display a popup on this page view. Nothing is
  // hidden with CSS, so no backdrop/scroll lock/focus trap can be left behind.
  // If the app runtime or this function is missing, this is a no-op.
  const popupGate = (() => {
    let applied = false;
    let appWasExhausted = false;
    let retry = 0;
    let keep = 0;
    let wanted = false;
    const actions = () => window.EcomSendApps?.actions;
    const sync = () => {
      const a = actions();
      if (!a || typeof a.setPopupQuotaExhausted !== 'function') {
        if (wanted && !retry) retry = window.setInterval(() => { if (actions()?.setPopupQuotaExhausted) { window.clearInterval(retry); retry = 0; sync(); } }, 500);
        return;
      }
      if (wanted && !applied) {
        appWasExhausted = a.popupQuotaExhausted === true;
        if (!appWasExhausted) a.setPopupQuotaExhausted(true);
        applied = true;
        // The app may (re)initialise its own state after we set it — e.g. when
        // an ad deep link starts the finder before the app has loaded. Keep
        // the flag asserted only while it is wanted.
        if (!appWasExhausted && !keep) keep = window.setInterval(() => { const x = actions(); if (wanted && x && x.popupQuotaExhausted !== true) x.setPopupQuotaExhausted(true); }, 400);
      } else if (!wanted && applied) {
        if (keep) { window.clearInterval(keep); keep = 0; }
        if (!appWasExhausted) a.setPopupQuotaExhausted(false);
        applied = false;
      }
    };
    const state = { finder: false, cart: false };
    return (key, value) => { state[key] = value; wanted = state.finder || state.cart; sync(); };
  })();

  function initFinder(root) {
    if (!root || root.dataset.wmhReady === 'true') return;
    root.dataset.wmhReady = 'true';

    const panel = root.querySelector('[data-wmh-panel]') || root;
    const shopTitle = root.querySelector('#WmhShopTitle');
    const compact = window.matchMedia('(max-width: 1099px)');
    const views = {
      intro: root.querySelector('[data-wmh-view="intro"]'),
      question: root.querySelector('[data-wmh-view="question"]'),
      result: root.querySelector('[data-wmh-view="result"]')
    };
    const qTitle = root.querySelector('[data-wmh-question-title]');
    const qChoices = root.querySelector('[data-wmh-choices]');
    const qCount = root.querySelector('[data-wmh-count]');
    const qBar = root.querySelector('[data-wmh-bar]');
    const backButton = root.querySelector('[data-wmh-back]');
    const track = root.querySelector('[data-wmh-track]');
    const position = root.querySelector('[data-wmh-position]');
    const prevButton = root.querySelector('[data-wmh-prev]');
    const nextButton = root.querySelector('[data-wmh-next]');
    const resultTitle = root.querySelector('[data-wmh-result-title]');
    const resultNav = root.querySelector('[data-wmh-result-nav]');
    const grindOptions = String(root.dataset.grindOptions || '').split('||').map((s) => s.trim()).filter(Boolean);

    let catalog = [];
    let packages = {};
    try {
      const seed = JSON.parse(root.querySelector('[data-wmh-catalog]')?.textContent || '{}');
      catalog = Array.isArray(seed.coffees) ? seed.coffees.map(prepareProduct) : [];
      packages = seed.packages || {};
    } catch (_) { catalog = []; }

    let answers = {};
    let history = [];
    let current = 'intro';
    let results = [];        // [{ product, variantIndex, grind, kind }]

    const setEngaged = (value) => {
      root.dataset.wmsEngaged = value ? 'true' : 'false';
      document.documentElement.toggleAttribute('data-wms-finder-engaged', value);
      popupGate('finder', value);
    };
    const miniCart = document.getElementById('wemond-mini-cart');
    if (miniCart && typeof MutationObserver === 'function') {
      new MutationObserver(() => popupGate('cart', miniCart.classList.contains('is-open'))).observe(miniCart, { attributes: true, attributeFilter: ['class'] });
    }

    const showView = (name) => {
      Object.entries(views).forEach(([key, el]) => { if (el) el.hidden = key !== name; });
      root.dataset.state = name;
      panel.dataset.state = name;
    };

    // Mobile/tablet: the section is either the four products ("shop") or
    // Kahvemi Bul ("finder"). Desktop shows both side by side and ignores it.
    const startLabels = root.querySelectorAll('[data-wmh-start-label]');
    const setMode = (mode) => {
      root.dataset.mode = mode;
      const resumable = current !== 'intro';
      startLabels.forEach((b) => { b.textContent = resumable ? 'Kahvemi Bul’a devam et' : 'Kahvemi Bul’u başlat'; });
    };

    // Keep the finder's top edge below the sticky header without jumping
    // when it is already comfortably in view.
    const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const keepInView = (behavior = 'smooth', target = panel) => {
      const headerH = headerHeight();
      const rect = target.getBoundingClientRect();
      if (rect.top >= headerH - 1 && rect.top <= window.innerHeight * 0.35) return;
      target.scrollIntoView({ behavior: reduceMotion() ? 'auto' : behavior, block: 'start' });
    };

    const renderQuestion = (key) => {
      const Q = QUESTIONS[key];
      const n = QUESTION_ORDER.indexOf(key) + 1;
      qTitle.textContent = Q.q;
      qCount.textContent = `Soru ${n} / ${QUESTION_ORDER.length}`;
      qBar.style.transform = `scaleX(${n / QUESTION_ORDER.length})`;
      qChoices.innerHTML = Q.o.map(([value, title, hint]) => `<button type="button" class="wmh-choice" data-value="${value}" aria-pressed="${answers[key] === value}"><b>${esc(title)}</b><span>${esc(hint)}</span></button>`).join('');
      backButton.hidden = n === 1;
      // After "Cevapları değiştir" the first question keeps earlier answers;
      // offer a clean reset there (desktop has no room for it on the results).
      root.querySelectorAll('.wmh-finder__reset').forEach((b) => { b.hidden = !(n === 1 && Object.keys(answers).length); });
    };

    const goQuestion = (key, { push = false, focus = true } = {}) => {
      if (push && current !== key) history.push(current);
      current = key;
      renderQuestion(key);
      showView('question');
      if (focus) qTitle.focus({ preventScroll: true });
    };

    // Starting again after "Kahvelere dön" resumes where the shopper left off
    // (same question, or the same results with their gram/grind choices).
    const start = (source) => {
      if (current === 'intro') {
        answers = {}; history = [];
        publish('wemond_finder_start', { source });
      }
      setEngaged(true);
      if (current === 'result' && results.length) showView('result');
      else goQuestion(current === 'intro' ? 'brew' : current, { focus: false });
      setMode('finder');
      if (current === 'result') requestAnimationFrame(updatePosition);
    };

    const focusCurrent = () => (current === 'result' ? resultTitle : qTitle).focus({ preventScroll: true });

    const exit = () => {
      publish('wemond_finder_skip', { from: current });
      setEngaged(false);
      setMode('shop');
      (shopTitle || root).scrollIntoView({ behavior: 'instant', block: 'start' });
      shopTitle?.focus({ preventScroll: true });
    };

    const pickExperiencePackage = (sole) => {
      let key;
      if (answers.brew === 'espresso') key = 'espresso';
      else if (answers.brew === 'filter') key = 'filter';
      else {
        const e = sole.paths.includes('espresso'); const f = sole.paths.includes('filter');
        if (e && !f) key = 'espresso'; else if (f && !e) key = 'filter';
        else key = contextScore(catalog, 'espresso', answers) > contextScore(catalog, 'filter', answers) ? 'espresso' : 'filter';
      }
      const pkg = packages?.[key];
      return pkg && pkg.available && pkg.url ? pkg : null;
    };

    const productCard = (entry, index, total) => {
      const p = entry.product;
      const v = p.variants[entry.variantIndex];
      const id = `wmh-grind-${p.handle}`;
      const productUrl = `${p.url}${p.variants.length > 1 ? `?variant=${v.id}` : ''}`;
      const grams = p.variants.length > 1
        ? `<div class="wmh-seg" role="group" aria-label="Gramaj">${p.variants.map((variant, i) => `<button type="button" data-wmh-gram="${i}" aria-pressed="${i === entry.variantIndex}">${esc(gramLabel(variant.title))}</button>`).join('')}</div>`
        : '';
      return `<article class="wmh-card" data-wmh-card="${index}" role="group" aria-roledescription="öneri" aria-label="Öneri ${index + 1} / ${total}: ${esc(cleanTitle(p.title))}">
        <a class="wmh-card__media" href="${esc(productUrl)}" tabindex="-1" aria-hidden="true">${p.featured_image ? `<img src="${esc(p.featured_image)}" alt="" width="600" height="451" loading="lazy">` : ''}</a>
        <div class="wmh-card__head">
          <h4 class="wmh-card__name">${esc(cleanTitle(p.title))}</h4>
          <a class="wmh-card__more" href="${esc(productUrl)}" data-wmh-more>Ürünü incele</a>
        </div>
        <p class="wmh-card__reason">${esc(reasonFor(p, answers))}</p>
        <div class="wmh-card__buy">
          <p class="wmh-card__price"><strong data-wmh-price>${money(v.price)}</strong> <span data-wmh-gram-label>${esc(gramLabel(v.title))}</span></p>
          ${grams}
        </div>
        <div class="wmh-card__order">
          <select id="${id}" data-wmh-grind aria-label="Öğütüm" aria-describedby="${id}-status"><option value="">Öğütüm seç</option>${grindOptions.map((g) => `<option value="${esc(g)}"${entry.grind === g ? ' selected' : ''}>${esc(g === 'Çekirdek' && answers.brew === 'espresso' ? 'Çekirdek · tam otomatik makine' : g)}</option>`).join('')}</select>
          <button type="button" class="wmh-card__add" data-wmh-add>Sepete ekle</button>
        </div>
        <p class="wmh-card__status" id="${id}-status" data-wmh-status role="status" aria-live="polite"></p>
      </article>`;
    };

    const packageCard = (pkg, index, total) => `<article class="wmh-card wmh-card--package" data-wmh-card="${index}" role="group" aria-roledescription="öneri" aria-label="Öneri ${index + 1} / ${total}: ${esc(pkg.title)}">
        <a class="wmh-card__media" href="${esc(pkg.url)}" tabindex="-1" aria-hidden="true">${pkg.featured_image ? `<img src="${esc(pkg.featured_image)}" alt="" width="600" height="451" loading="lazy">` : ''}</a>
        <div class="wmh-card__head"><h4 class="wmh-card__name">${esc(pkg.title)}</h4></div>
        <p class="wmh-card__reason">Seçimine yakın kahveleri tek pakette dene; üç kahve, toplam 750 g, yalnız çekirdek gönderilir.</p>
        <div class="wmh-card__buy">
          <p class="wmh-card__price"><strong>${money(pkg.price)}</strong>${pkg.price_varies ? '<span>’den</span>' : ''} <span>750 g</span></p>
        </div>
        <div class="wmh-card__order">
          <a class="wmh-card__add wmh-card__add--wide" href="${esc(pkg.url)}" data-wmh-more>Paketi oluştur</a>
        </div>
      </article>`;

    const updatePosition = () => {
      if (!track || results.length < 2) return;
      const cards = [...track.children];
      const trackRect = track.getBoundingClientRect();
      let active = 0; let best = Infinity;
      cards.forEach((card, i) => { const d = Math.abs(card.getBoundingClientRect().left - trackRect.left); if (d < best) { best = d; active = i; } });
      position.textContent = `${active + 1} / ${cards.length}`;
      prevButton.setAttribute('aria-disabled', String(active === 0));
      nextButton.setAttribute('aria-disabled', String(active === cards.length - 1));
      track.dataset.active = String(active);
    };

    const scrollToCard = (index, { report = true } = {}) => {
      const card = track.children[index];
      if (!card) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: card.offsetLeft - track.offsetLeft - parseFloat(getComputedStyle(track).paddingLeft || 0), behavior: reduce ? 'auto' : 'smooth' });
      if (report) publish('wemond_finder_result_switch', { to: index + 1 });
    };

    const renderResults = () => {
      const ranked = rankPool(eligiblePool(catalog, answers.brew), answers);
      if (!ranked.length) {
        results = [];
        resultTitle.textContent = 'Uygun stokta kahve bulamadık';
        track.innerHTML = `<div class="wmh-empty"><p>${catalog.length ? 'Seçtiğin demleme yöntemine uygun stokta kahve şu an yok.' : 'Kahve listesi şu anda yüklenemedi.'}</p><a href="${esc(root.dataset.allUrl || '/collections/all')}">Tüm kahvelere göz at</a></div>`;
        resultNav.hidden = true;
        return;
      }
      const picks = ranked.slice(0, 2).map((product) => ({ product, variantIndex: 0, grind: '', kind: 'product' }));
      if (picks.length === 1) { const pkg = pickExperiencePackage(picks[0].product); if (pkg) picks.push({ pkg, kind: 'package' }); }
      results = picks;
      const total = picks.length;
      resultTitle.textContent = total > 1 ? 'Sana uygun iki öneri' : 'Sana uygun kahve';
      track.innerHTML = picks.map((entry, i) => entry.kind === 'package' ? packageCard(entry.pkg, i, total) : productCard(entry, i, total)).join('');
      resultNav.hidden = total < 2;
      track.scrollLeft = 0;
      requestAnimationFrame(updatePosition);
      publish('wemond_finder_complete', { answers: { ...answers }, results: picks.map((e) => e.product?.handle || e.pkg?.handle) });
    };

    const showResult = () => {
      history.push(current);
      current = 'result';
      renderResults();
      showView('result');
      resultTitle.focus({ preventScroll: true });
      keepInView();
    };

    const choose = (value) => {
      const key = current;
      const index = QUESTION_ORDER.indexOf(key);
      // A changed answer invalidates later answers; re-confirming the same
      // answer (e.g. after "Cevapları değiştir") keeps them.
      if (answers[key] !== value) QUESTION_ORDER.slice(index + 1).forEach((later) => { delete answers[later]; });
      answers[key] = value;
      publish('wemond_finder_answer', { step: index + 1, question: key, value });
      if (key === 'brew' && !eligiblePool(catalog, value).length) { showResult(); return; }
      const next = QUESTION_ORDER[index + 1];
      if (next) { goQuestion(next, { push: true }); keepInView(); } else showResult();
    };

    const goBack = () => {
      const previous = history.pop();
      if (previous && previous !== 'result' && previous !== 'intro') { goQuestion(previous); keepInView(); }
    };

    const edit = () => { history = []; goQuestion('brew'); keepInView(); };
    // Desktop returns to the Kahvemi Bul start; mobile/tablet has no start
    // screen inside the finder, so it goes straight to the first question.
    const restart = () => {
      answers = {}; history = []; results = []; current = 'intro';
      if (compact.matches) { publish('wemond_finder_start', { source: 'restart' }); goQuestion('brew'); keepInView(); return; }
      setEngaged(false);
      showView('intro');
      setMode('shop');
      panel.querySelector('[data-wmh-start]')?.focus({ preventScroll: true });
      keepInView();
    };

    // ---- Cart -------------------------------------------------------------
    const rootUrl = () => window.Shopify?.routes?.root || '/';
    const refreshBubble = async () => {
      try {
        const res = await fetch(`${rootUrl()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        const cart = res.ok ? await res.json() : null;
        const icon = document.querySelector('cart-icon');
        if (cart && icon && typeof icon.renderCartBubble === 'function') await icon.renderCartBubble(cart.item_count || 0);
      } catch (_) {}
    };

    const addToCart = async (card) => {
      const entry = results[Number(card.dataset.wmhCard)];
      if (!entry || entry.kind !== 'product') return;
      const button = card.querySelector('[data-wmh-add]');
      const status = card.querySelector('[data-wmh-status]');
      const select = card.querySelector('[data-wmh-grind]');
      if (button.dataset.busy === 'true') return;
      // Missing grind: the select itself says so (no extra line that would
      // change the card height); the status line carries it for screen readers.
      if (!select.value) {
        select.setAttribute('aria-invalid', 'true');
        if (select.options[0]) select.options[0].textContent = 'Önce öğütüm seç';
        status.textContent = 'Önce öğütüm seç.';
        status.dataset.tone = 'error';
        delete status.dataset.show;
        select.focus();
        return;
      }
      const product = entry.product;
      const variant = product.variants[entry.variantIndex];
      button.dataset.busy = 'true';
      button.setAttribute('aria-disabled', 'true');
      window.clearTimeout(Number(button.dataset.resetTimer || 0));
      button.textContent = 'Ekleniyor…';
      status.textContent = 'Ekleniyor…';
      status.dataset.tone = '';
      delete status.dataset.show;
      let added = false;
      try {
        const response = await fetch(`${rootUrl()}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variant.id), quantity: 1, properties: { 'Öğütüm': select.value } }] })
        });
        let payload = {};
        try { payload = await response.json(); } catch (_) {}
        if (!response.ok) throw new Error(payload.description || payload.message || 'Ürün sepete eklenemedi.');
        const line = (payload.items || [])[0];
        if (line && Number(line.variant_id) !== Number(variant.id)) throw new Error('Sepete farklı bir ürün eklendi; lütfen sepeti kontrol et.');
        status.textContent = `${gramLabel(variant.title)} · ${select.value} sepete eklendi.`;
        status.dataset.tone = 'success';
        added = true;
        publish('wemond_finder_add_to_cart', { handle: product.handle, variant_id: variant.id, gram: gramValue(variant.title), grind: select.value, price: variant.price / 100, currency: 'TRY' });
        await refreshBubble();
        document.dispatchEvent(new CustomEvent(CART_EVENT, { bubbles: true, detail: { action: 'add', source: 'wemond-hero-finder' } }));
      } catch (error) {
        status.textContent = error?.message || 'Bir hata oluştu, tekrar dene.';
        status.dataset.tone = 'error';
        status.dataset.show = 'true';
      } finally {
        delete button.dataset.busy;
        button.removeAttribute('aria-disabled');
        button.textContent = added ? 'Eklendi ✓' : 'Sepete ekle';
        if (added) button.dataset.resetTimer = String(window.setTimeout(() => { button.textContent = 'Sepete ekle'; }, 2500));
      }
    };

    const setGram = (card, index) => {
      const entry = results[Number(card.dataset.wmhCard)];
      const product = entry.product;
      entry.variantIndex = index;
      const v = product.variants[index];
      card.querySelectorAll('[data-wmh-gram]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.wmhGram) === index)));
      card.querySelector('[data-wmh-price]').textContent = money(v.price);
      card.querySelector('[data-wmh-gram-label]').textContent = gramLabel(v.title);
      const url = `${product.url}?variant=${v.id}`;
      card.querySelectorAll('a[data-wmh-more], .wmh-card__media').forEach((a) => a.setAttribute('href', url));
      const status = card.querySelector('[data-wmh-status]');
      if (status.dataset.tone === 'success') { status.textContent = ''; status.dataset.tone = ''; }
    };

    // ---- Events -----------------------------------------------------------
    // ---- Step transitions -------------------------------------------------
    // One short, calm change per step: the new view fades in with a few px of
    // movement while the section height eases from the old to the new size
    // (its natural height; nothing is pinned to a large fixed height). While
    // a step runs, further step clicks are ignored, so a fast double tap can
    // never answer two questions.
    const STEP_MS = 220;
    const STEP_LOCK_MS = 380;
    let stepping = false;
    const visibleParts = () => [...root.querySelectorAll('[data-wmh-view]:not([hidden]), [data-wmh-shop-part]')].filter((el) => el.getClientRects().length);
    const step = (mutate) => {
      if (stepping) return;
      stepping = true;
      if (reduceMotion() || typeof root.animate !== 'function') { mutate(); window.setTimeout(() => { stepping = false; }, STEP_LOCK_MS); return; }
      const before = new Set(visibleParts());
      const beforeQ = qTitle.textContent;
      const h0 = root.getBoundingClientRect().height;
      mutate();
      const h1 = root.getBoundingClientRect().height;
      const entering = visibleParts().filter((el) => !before.has(el) || (el === views.question && qTitle.textContent !== beforeQ));
      if (root.dataset.mode === 'finder' && compact.matches && !before.has(panel)) entering.push(panel);
      entering.forEach((el) => { el.classList.remove('wmh-enter'); void el.offsetWidth; el.classList.add('wmh-enter'); });
      if (Math.abs(h1 - h0) > 4) {
        root.classList.add('is-animating');
        const anim = root.animate([{ height: `${h0}px` }, { height: `${h1}px` }], { duration: STEP_MS, easing: 'cubic-bezier(.2,.6,.2,1)' });
        anim.onfinish = anim.oncancel = () => root.classList.remove('is-animating');
      }
      // The lock outlasts the animation a little, so a quick double tap
      // (two taps ~100–300 ms apart) still counts as one answer.
      window.setTimeout(() => entering.forEach((el) => el.classList.remove('wmh-enter')), STEP_MS + 20);
      window.setTimeout(() => { stepping = false; }, STEP_LOCK_MS);
    };

    root.addEventListener('click', (event) => {
      const t = event.target.closest('button, a');
      if (!t || !root.contains(t)) return;
      if (t.matches('[data-wmh-start]')) { step(() => { start('button'); focusCurrent(); keepInView(compact.matches ? 'auto' : 'smooth'); }); return; }
      if (t.matches('[data-wmh-exit]')) { step(exit); return; }
      if (t.matches('.wmh-choice')) { step(() => choose(t.dataset.value)); return; }
      if (t.matches('[data-wmh-back]')) { step(goBack); return; }
      if (t.matches('[data-wmh-edit]')) { step(edit); return; }
      if (t.matches('[data-wmh-restart]')) { step(restart); return; }
      if (t.matches('[data-wmh-prev]')) { if (t.getAttribute('aria-disabled') !== 'true') scrollToCard(Number(track.dataset.active || 0) - 1); return; }
      if (t.matches('[data-wmh-next]')) { if (t.getAttribute('aria-disabled') !== 'true') scrollToCard(Number(track.dataset.active || 0) + 1); return; }
      const card = t.closest('[data-wmh-card]');
      if (card && t.matches('[data-wmh-gram]')) { setGram(card, Number(t.dataset.wmhGram)); return; }
      if (card && t.matches('[data-wmh-add]')) { addToCart(card); return; }
      if (card && t.matches('[data-wmh-more]')) publish('wemond_finder_result_click', { position: Number(card.dataset.wmhCard) + 1 });
    });

    root.addEventListener('change', (event) => {
      const select = event.target.closest('[data-wmh-grind]');
      if (!select) return;
      const card = select.closest('[data-wmh-card]');
      results[Number(card.dataset.wmhCard)].grind = select.value;
      select.removeAttribute('aria-invalid');
      if (select.options[0]) select.options[0].textContent = 'Öğütüm seç';
      const status = card.querySelector('[data-wmh-status]');
      if (status.dataset.tone === 'error') { status.textContent = ''; status.dataset.tone = ''; delete status.dataset.show; }
    });

    track?.addEventListener('scroll', () => window.requestAnimationFrame(updatePosition), { passive: true });
    track?.addEventListener('focusin', (event) => {
      // Tabbing into the second card: bring the whole card into view instead
      // of a half-scrolled position.
      const card = event.target.closest('[data-wmh-card]');
      if (card && Number(card.dataset.wmhCard) !== Number(track.dataset.active || 0)) scrollToCard(Number(card.dataset.wmhCard), { report: false });
    });
    track?.addEventListener('keydown', (event) => {
      if (event.target.matches('select')) return;
      if (event.key === 'ArrowRight' && event.target === track) { event.preventDefault(); scrollToCard(Number(track.dataset.active || 0) + 1); }
      if (event.key === 'ArrowLeft' && event.target === track) { event.preventDefault(); scrollToCard(Number(track.dataset.active || 0) - 1); }
    });
    window.addEventListener('resize', () => { if (current === 'result') updatePosition(); }, { passive: true });

    // Links elsewhere on the page ("Kahvemi bul" in other sections) start the
    // inline flow instead of the removed modal.
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.(OPEN_SELECTOR);
      if (!trigger || root.contains(trigger)) return;
      event.preventDefault();
      start('link');
      panel.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      focusCurrent();
    });

    // Ad deep links: ?open=coffee-finder or #wemond-coffee-finder. The URL is
    // left untouched (UTM and preview parameters stay).
    const deepLink = () => {
      const fromQuery = new URLSearchParams(window.location.search).get('open') === DEEP_LINK_QUERY_VALUE;
      const fromHash = window.location.hash === DEEP_LINK_HASH;
      if (!fromQuery && !fromHash) return;
      start(fromQuery ? 'deeplink_query' : 'deeplink_hash');
      const go = () => { panel.scrollIntoView({ behavior: 'auto', block: 'start' }); qTitle.focus({ preventScroll: true }); };
      requestAnimationFrame(go);
      // Late layout shifts (fonts/images) can move the hero; re-align once.
      window.addEventListener('load', () => { if (current === 'brew' && Math.abs(panel.getBoundingClientRect().top - headerHeight()) > 40) go(); }, { once: true });
    };
    window.addEventListener('hashchange', () => { if (window.location.hash === DEEP_LINK_HASH) { start('deeplink_hash'); panel.scrollIntoView({ block: 'start' }); focusCurrent(); } });

    showView('intro');
    setEngaged(false);
    setMode('shop');
    deepLink();

    // Test hook for the QA script (read-only state).
    root.wmhState = () => ({ mode: root.dataset.mode, current, answers: { ...answers }, results: results.map((e) => e.kind === 'product' ? { handle: e.product.handle, variant: e.product.variants[e.variantIndex], grind: e.grind } : { handle: e.pkg.handle }) });
  }

  // While Shopify's consent banner is on screen, reserve its real height at
  // the bottom of the page so the last controls can always be scrolled above
  // it. Consent mechanics are not touched.
  const watchConsentBanner = () => {
    const html = document.documentElement;
    let observed = null;
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(() => update()) : null;
    const update = () => {
      const banner = document.getElementById('shopify-pc__banner');
      if (banner && banner !== observed) {
        observed = banner;
        ro?.observe(banner);
        if (typeof MutationObserver === 'function') new MutationObserver(update).observe(banner, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      }
      const visible = banner && getComputedStyle(banner).display !== 'none' && getComputedStyle(banner).visibility !== 'hidden' && banner.getBoundingClientRect().height > 0;
      if (visible) {
        html.style.setProperty('--wmh-consent-h', `${Math.ceil(banner.getBoundingClientRect().height)}px`);
        html.setAttribute('data-wmh-consent-open', '');
      } else {
        html.removeAttribute('data-wmh-consent-open');
        html.style.removeProperty('--wmh-consent-h');
      }
    };
    if (typeof MutationObserver === 'function') new MutationObserver(update).observe(document.body, { childList: true });
    document.addEventListener('visitorConsentCollected', () => setTimeout(update, 50));
    window.addEventListener('resize', update, { passive: true });
    update();
  };

  const init = (scope = document) => scope.querySelectorAll?.(ROOT).forEach(initFinder);
  const boot = () => { init(); if (document.querySelector(ROOT)) watchConsentBanner(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
